import type { Query } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { UserRole } from "../auth/auth";
import { logPurchase } from "../misc/firebase";
import type { CalculationVersion, SavedCalculation } from "./calculationTypes";
import {
  deleteCalculation,
  getCalculation,
  orderACalculation,
  updateCalculation,
  updateCalculationPrices,
} from "./calculationsApi";

const CALCULATIONS_QUERY_KEY = "calculations";

function isCalculationsListQuery(query: Query) {
  return query.queryKey[0] === CALCULATIONS_QUERY_KEY && typeof query.queryKey[1] === "object";
}

export function useDeleteCalculation() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<{ calculationId: string }, unknown, { calculationId: string }>({
    mutationFn: async ({ calculationId }) => {
      await deleteCalculation(calculationId);
      return { calculationId };
    },
    onSuccess: async ({ calculationId }) => {
      enqueueSnackbar("Kalkulacja została usunięta", { variant: "success" });
      await queryClient.invalidateQueries({
        predicate: isCalculationsListQuery,
      });
      queryClient.removeQueries({ queryKey: [CALCULATIONS_QUERY_KEY, calculationId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć kalkulacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });
}

export const useCalculation = () => {
  const { calculationId: calculationIdParam } = useParams<{ calculationId: string }>();
  const calculationId = calculationIdParam!;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: calculation, isLoading } = useQuery({
    queryKey: ["calculations", calculationId],
    queryFn: ({ signal }) => getCalculation(calculationId, signal),
  });

  const deleteCalculationMutation = useDeleteCalculation();

  const updateCalculationMutation = useMutation<
    SavedCalculation,
    unknown,
    { update: CalculationVersion; role: UserRole }
  >({
    mutationFn: ({ update }) => updateCalculation(calculationId, update),
    onSuccess: async (data, { role }) => {
      const isCustomerOnDemandCalculation = role === "CUSTOMER" && data.versions.at(-1)?.price === "ON_DEMAND";
      const additionalInfo = isCustomerOnDemandCalculation
        ? " Twoje zapytanie zostało wysłane do dostawcy. Link do uzupełnionej wyceny otrzymasz droga mailową."
        : "";
      enqueueSnackbar(`Nowa wersja kalkulacji została utworzona.${additionalInfo}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: isCalculationsListQuery });
      queryClient.setQueryData([CALCULATIONS_QUERY_KEY, calculationId], data);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować kalkulacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateCalculationPricesMutation = useMutation<
    { calculation: SavedCalculation; wasModified: boolean },
    unknown,
    void
  >({
    mutationFn: () => updateCalculationPrices(calculationId),
    onSuccess: async ({ wasModified, calculation }) => {
      if (wasModified) {
        await queryClient.invalidateQueries({ predicate: isCalculationsListQuery });
        queryClient.setQueryData([CALCULATIONS_QUERY_KEY, calculationId], calculation);
        enqueueSnackbar("Ceny produktów w kalkulacji zostały zaktualizowane", { variant: "success" });
      } else {
        enqueueSnackbar("Ceny produktów w kalkulacji nie zmieniły się", { variant: "success" });
      }
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować cen produktów w kalkulacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const orderCalculationMutation = useMutation<
    SavedCalculation,
    unknown,
    { version: number; orderFromProviders: boolean }
  >({
    mutationFn: ({ version, orderFromProviders }) => orderACalculation(calculationId, version, orderFromProviders),
    onSuccess: async (data, { version }) => {
      enqueueSnackbar("Zamówienie zostało złożone", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isCalculationsListQuery });
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "orders" && typeof q.queryKey[1] === "object",
      });
      queryClient.setQueryData([CALCULATIONS_QUERY_KEY, calculationId], data);
      const orderedVersion = data.versions[version - 1];
      logPurchase(
        data.orderId,
        orderedVersion.products.map((p) => ({
          id: p.id,
          value: typeof p.price === "number" ? p.price : undefined,
          currency: orderedVersion.currency,
        })),
      );
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się złożyć zamówienia. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    calculation,
    isLoading,
    onDelete: () => deleteCalculationMutation.mutateAsync({ calculationId }),
    onUpdate: (calculation: CalculationVersion, role: UserRole) =>
      updateCalculationMutation.mutateAsync({ update: calculation, role }),
    onUpdatePrices: () => updateCalculationPricesMutation.mutateAsync(),
    /** `version` is the 1-based version number (as in the API), not the index into `calculation.versions`. */
    onOrder: (version: number, orderFromProviders: boolean) =>
      orderCalculationMutation.mutateAsync({ version, orderFromProviders }),
  };
};
